import { useFeatureFlags } from '@brightdrop/feature-flags-client';
import type { FunctionComponent, SVGProps } from 'react';
import GmFleetLogoSvg from '../../assets/gm-fleet-logo.svg?react';
import OnStarLogoSvg from '../../assets/OnStar-logo.svg?react';
import OnStarFleetIntelligenceLogoSvg from '../../assets/onStar-fleet-intelligence-logo.svg?react';
import {
  BRAND_NAME,
  LEGACY_BRAND_NAME,
  ONSTAR_FLEET_INTELLIGENCE_REBRAND_FLAG,
} from '../../brand/brand.constants';

type BrandLogoConfig = {
  component: FunctionComponent<SVGProps<SVGSVGElement>>;
  ariaLabel: string;
  defaultWidth: number;
  defaultHeight: number;
};

// Logo accessible names reuse the same brand labels as UI copy
// (`@brightdrop/bd-ui/brand`) so the spoken brand stays consistent everywhere.
const GM_FLEET_LOGO_CONFIG: BrandLogoConfig = {
  component: GmFleetLogoSvg,
  ariaLabel: LEGACY_BRAND_NAME,
  defaultWidth: 122,
  defaultHeight: 48,
};

const ONSTAR_FLEET_INTELLIGENCE_LOGO_CONFIG: BrandLogoConfig = {
  component: OnStarFleetIntelligenceLogoSvg,
  ariaLabel: BRAND_NAME,
  defaultWidth: 344,
  defaultHeight: 48,
};

const ONSTAR_MONOGRAM_LOGO_CONFIG: BrandLogoConfig = {
  component: OnStarLogoSvg,
  ariaLabel: BRAND_NAME,
  defaultWidth: 32,
  defaultHeight: 32,
};

const getBrandLogoConfig = (
  isOnStarRebrandEnabled: boolean,
  isNavCollapsed: boolean
): BrandLogoConfig => {
  if (!isOnStarRebrandEnabled) {
    return GM_FLEET_LOGO_CONFIG;
  }

  return isNavCollapsed
    ? ONSTAR_MONOGRAM_LOGO_CONFIG
    : ONSTAR_FLEET_INTELLIGENCE_LOGO_CONFIG;
};

type BrandLogoProps = {
  width?: number;
  height?: number;
  className?: string;
  id?: string;
  'data-testid'?: string;
  isNavCollapsed?: boolean;
  'aria-hidden'?: boolean;
};

const BrandLogo = ({
  width,
  height,
  className,
  id,
  'data-testid': dataTestId,
  isNavCollapsed = false,
  'aria-hidden': ariaHidden = false,
}: BrandLogoProps): JSX.Element => {
  const { getFlag } = useFeatureFlags();
  const isOnStarRebrandEnabled = Boolean(
    getFlag(ONSTAR_FLEET_INTELLIGENCE_REBRAND_FLAG)
  );

  const {
    component: LogoComponent,
    ariaLabel,
    defaultWidth,
    defaultHeight,
  } = getBrandLogoConfig(isOnStarRebrandEnabled, isNavCollapsed);

  return (
    <LogoComponent
      width={width ?? defaultWidth}
      height={height ?? defaultHeight}
      className={className}
      preserveAspectRatio="xMinYMid meet"
      role={ariaHidden ? undefined : 'img'}
      aria-label={ariaHidden ? undefined : ariaLabel}
      aria-hidden={ariaHidden || undefined}
      id={id}
      data-testid={dataTestId}
    />
  );
};

export default BrandLogo;
